/**
 * V124 — Payment Links admin panel (Feb-2026).
 *
 * Two tabs:
 *   • Links    — table of all admin-generated /pay/<id> links + Create modal + status controls
 *   • Accounts — CRUD for payment_accounts (bank + UPI + Razorpay handle) + default toggle
 *
 * Both flows use the existing admin JWT via the shared `api` helper.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Plus, Copy, ExternalLink, Trash2, Edit3, X, Check, RefreshCw,
    Search, IndianRupee, CheckCircle2, XCircle, Clock, Ban, Star, StarOff,
    Building2, Smartphone, MessageCircle, Image as ImageIcon,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import PaymentLinkFormModal, { AdminOnlyBadge } from "./PaymentLinkFormModal";
import { ClientResults, LinkHistoryModal } from "./PaymentLinkClients";
import { PUBLIC_BASE, fmtInr, fmtDate, waHref, clientPrefill } from "./paymentLinkUtils";

const STATUS_PILLS = {
    pending:   { label: "Pending",   icon: Clock,        cls: "bg-amber-50 text-amber-800 border-amber-200" },
    paid:      { label: "Paid",      icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    failed:    { label: "Failed",    icon: XCircle,      cls: "bg-rose-50 text-rose-800 border-rose-200" },
    cancelled: { label: "Cancelled", icon: Ban,          cls: "bg-slate-100 text-slate-600 border-slate-200" },
};


/* ========================================================================== */

export default function PaymentLinksPanel() {
    const [subTab, setSubTab] = useState("links");
    return (
        <div className="space-y-6" data-testid="payment-links-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A]">Payment Management</h2>
                    <p className="text-sm text-slate-500">Generate amount-locked payment links + manage bank / UPI / Razorpay accounts.</p>
                </div>
                <div className="inline-flex bg-slate-100 rounded-lg p-1" role="tablist">
                    <button
                        onClick={() => setSubTab("links")}
                        className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${subTab === "links" ? "bg-white shadow text-[#1E3A8A]" : "text-slate-600 hover:text-[#0F172A]"}`}
                        data-testid="payment-tab-links"
                    >
                        Payment Links
                    </button>
                    <button
                        onClick={() => setSubTab("accounts")}
                        className={`px-4 py-1.5 rounded-md text-sm font-semibold transition ${subTab === "accounts" ? "bg-white shadow text-[#1E3A8A]" : "text-slate-600 hover:text-[#0F172A]"}`}
                        data-testid="payment-tab-accounts"
                    >
                        Payment Accounts
                    </button>
                </div>
            </div>
            {subTab === "links" ? <LinksTab /> : <AccountsTab />}
        </div>
    );
}

/* ========================================================================== */
/* LINKS TAB                                                                  */
/* ========================================================================== */

function LinksTab() {
    const [items, setItems] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    // V182 — one modal for create / edit / new-request-for-client.
    const [modal, setModal] = useState(null); // { editing?, prefill? }
    const [historyLink, setHistoryLink] = useState(null);
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [linksRes, acctsRes] = await Promise.all([
                api.get("/admin/payment/links", { params: filter === "all" ? {} : { status: filter } }),
                api.get("/admin/payment/accounts"),
            ]);
            setItems(linksRes.data.items || []);
            setAccounts(acctsRes.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    // V182 — Server-side client search (private search name, names, phone digits, enquiry ID).
    const searchClients = useCallback(async (term) => {
        if (!term.trim()) { setClients([]); return; }
        setClientsLoading(true);
        try {
            const res = await api.get("/admin/payment/clients", { params: { q: term.trim() } });
            setClients(res.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setClientsLoading(false);
        }
    }, []);
    useEffect(() => {
        const t = setTimeout(() => searchClients(q), 300);
        return () => clearTimeout(t);
    }, [q, searchClients]);
    const reloadAll = () => { load(); searchClients(q); };
    const openEdit = (it) => setModal({ editing: it });
    const openNewFrom = (it) => setModal({ prefill: clientPrefill(it, it.id) });

    const filteredItems = useMemo(() => {
        if (!q.trim()) return items;
        const s = q.trim().toLowerCase();
        const digits = s.replace(/\D/g, "");
        return items.filter((it) =>
            (it.client_search_name || "").toLowerCase().includes(s) ||
            (digits.length >= 3 && (it.contact_number || "").includes(digits)) ||
            (it.student_name || "").toLowerCase().includes(s) ||
            (it.parent_name || "").toLowerCase().includes(s) ||
            (it.enquiry_id || "").toLowerCase().includes(s) ||
            (it.id || "").toLowerCase().includes(s) ||
            (it.description || "").toLowerCase().includes(s) ||
            String(it.amount).includes(s)
        );
    }, [items, q]);

    const totals = useMemo(() => {
        const t = { pending: 0, paid: 0, failed: 0, cancelled: 0, all: items.length };
        items.forEach((it) => { if (t[it.status] !== undefined) t[it.status] += 1; });
        return t;
    }, [items]);

    const activeAccounts = accounts.filter((a) => a.is_active);

    const handleUpdateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/payment/links/${id}`, { status });
            toast.success(`Marked ${status}`);
            reloadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this pending link? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/payment/links/${id}`);
            toast.success("Link deleted");
            reloadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-4">
            {/* Toolbar — mobile stacks, desktop inlines */}
            <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setModal({})}
                        disabled={activeAccounts.length === 0}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold px-4 py-2.5 rounded-lg text-sm shadow disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="payment-links-new-btn"
                    >
                        <Plus className="w-4 h-4" /> Create Payment Link
                    </button>
                    <button
                        onClick={reloadAll}
                        className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 font-medium px-3 py-2.5 rounded-lg text-sm"
                        data-testid="payment-links-refresh"
                        aria-label="Refresh"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        <span className="hidden sm:inline">Refresh</span>
                    </button>
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search client, student, parent, phone number or enquiry ID..."
                        className="w-full pl-9 pr-3 py-3 border-2 border-slate-300 rounded-xl text-base sm:text-sm focus:outline-none focus:border-[#1E3A8A] bg-white shadow-sm"
                        data-testid="payment-links-search"
                    />
                </div>
            </div>

            {/* Status filters — horizontal scroll pills, always full-visible on mobile */}
            <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 no-scrollbar">
                {["all", "pending", "paid", "failed", "cancelled"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                            filter === s ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#1E3A8A]"
                        }`}
                        data-testid={`payment-links-filter-${s}`}
                    >
                        {s === "all" ? "All" : STATUS_PILLS[s].label} · {totals[s]}
                    </button>
                ))}
            </div>

            <ClientResults clients={clients} loading={clientsLoading} q={q} onEdit={openEdit} onHistory={setHistoryLink}
                onCreateFor={(prefill) => setModal({ prefill })} />

            {activeAccounts.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                    You need at least one <strong>active Payment Account</strong> before you can create links. Switch to the <strong>Payment Accounts</strong> tab above to add one.
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : filteredItems.length === 0 ? (
                <div className="py-16 text-center text-slate-500">
                    <p className="mb-2">No payment links yet.</p>
                    <button
                        onClick={() => setModal({})}
                        disabled={activeAccounts.length === 0}
                        className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold text-sm hover:underline disabled:opacity-50"
                    >
                        <Plus className="w-4 h-4" /> Create your first link
                    </button>
                </div>
            ) : (
                <>
                    {/* Mobile: card list (< md) */}
                    <div className="md:hidden space-y-3" data-testid="payment-links-mobile-list">
                        {filteredItems.map((it) => (
                            <MobileLinkCard
                                key={it.id}
                                item={it}
                                url={`${PUBLIC_BASE}/pay/${it.id}`}
                                onStatus={handleUpdateStatus}
                                onDelete={handleDelete}
                                onEdit={openEdit}
                                onNewFrom={openNewFrom}
                                onHistory={setHistoryLink}
                            />
                        ))}
                    </div>

                    {/* Desktop: table (>= md) */}
                    <div className="hidden md:block overflow-x-auto bg-white rounded-lg border border-slate-200">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Link</th>
                                    <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Amount</th>
                                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">For</th>
                                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Account</th>
                                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Status</th>
                                    <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Created</th>
                                    <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((it) => {
                                    const StatusIcon = STATUS_PILLS[it.status]?.icon || Clock;
                                    const url = `${PUBLIC_BASE}/pay/${it.id}`;
                                    return (
                                        <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`payment-link-row-${it.id}`}>
                                            <td className="px-4 py-3 font-mono text-xs text-slate-600">{it.id}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-[#0F172A]">
                                                <div className="font-bold">{fmtInr(it.amount)}</div>
                                                {it.session_count && <div className="text-[10px] text-slate-500" data-testid={`payment-link-sessions-${it.id}`}>{it.session_count} sessions</div>}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700 max-w-[260px]">
                                                {it.client_search_name && (
                                                    <div className="flex items-center gap-1.5 min-w-0" data-testid={`payment-link-client-name-${it.id}`}>
                                                        <span className="truncate font-semibold text-violet-800">{it.client_search_name}</span><AdminOnlyBadge />
                                                    </div>
                                                )}
                                                <div className="truncate font-medium">{it.student_name || it.parent_name || "—"}</div>
                                                {it.contact_number && <div className="text-[10px] text-emerald-700">+{it.contact_number}</div>}
                                                {it.description && <div className="text-[11px] text-slate-500 truncate">{it.description}</div>}
                                                {it.enquiry_id && <div className="text-[10px] text-slate-400 mt-0.5">ID: {it.enquiry_id}</div>}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-600 max-w-[180px] truncate">{it.payment_account_label || "—"}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_PILLS[it.status]?.cls || ""}`}>
                                                    <StatusIcon className="w-3 h-3" /> {STATUS_PILLS[it.status]?.label}
                                                </span>
                                                {it.has_screenshot && (
                                                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-slate-500">
                                                        <ImageIcon className="w-3 h-3" /> Screenshot
                                                    </span>
                                                )}
                                                {it.parent_confirmed_at && it.status === "pending" && (
                                                    <div className="text-[10px] text-emerald-600 mt-0.5">Parent confirmed at {fmtDate(it.parent_confirmed_at)}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-500">
                                                <div>{fmtDate(it.created_at)}</div>
                                                <div className="text-[10px] text-slate-400">{it.created_by || "—"}</div>
                                                {it.edit_history?.length > 0 && <div className="text-[10px] text-slate-400">Edited {fmtDate(it.updated_at)}</div>}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
                                                        className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                                                        title="Copy link"
                                                        data-testid={`payment-link-copy-${it.id}`}
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </button>
                                                    <a
                                                        href={url}
                                                        target="_blank" rel="noopener noreferrer"
                                                        className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                                                        title="Open"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                    </a>
                                                    {it.status === "pending" && (
                                                        <a href={waHref(it)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                                                            title={it.contact_number ? `WhatsApp +${it.contact_number}` : "WhatsApp"} data-testid={`payment-link-row-whatsapp-${it.id}`}>
                                                            <MessageCircle className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    <button onClick={() => setHistoryLink(it)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="History" data-testid={`payment-link-history-${it.id}`}>
                                                        <Clock className="w-4 h-4" />
                                                    </button>
                                                    {it.status === "pending" ? (
                                                        <button onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-blue-100 text-[#1E3A8A]" title="Edit" data-testid={`payment-link-edit-${it.id}`}>
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => openNewFrom(it)} className="p-1.5 rounded hover:bg-blue-100 text-[#1E3A8A]" title="New linked request" data-testid={`payment-link-new-from-${it.id}`}>
                                                            <Plus className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {it.status === "pending" && (
                                                        <>
                                                            <button
                                                                onClick={() => handleUpdateStatus(it.id, "paid")}
                                                                className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                                                                title="Mark paid"
                                                                data-testid={`payment-link-mark-paid-${it.id}`}
                                                            >
                                                                <Check className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleUpdateStatus(it.id, "cancelled")}
                                                                className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                                                                title="Cancel link"
                                                            >
                                                                <Ban className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(it.id)}
                                                                className="p-1.5 rounded hover:bg-rose-100 text-rose-600"
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {modal && (
                <PaymentLinkFormModal
                    accounts={activeAccounts}
                    editing={modal.editing || null}
                    prefill={modal.prefill || null}
                    onClose={() => setModal(null)}
                    onSaved={reloadAll}
                />
            )}
            {historyLink && <LinkHistoryModal link={historyLink} onClose={() => setHistoryLink(null)} />}
        </div>
    );
}

/* ========================================================================== */

/* ========================================================================== */
/* ACCOUNTS TAB                                                               */
/* ========================================================================== */

function AccountsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // account or "new"

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/payment/accounts");
            setItems(res.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setDefault = async (id) => {
        try {
            await api.post(`/admin/payment/accounts/${id}/set-default`);
            toast.success("Default account updated");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const toggleActive = async (a) => {
        try {
            await api.patch(`/admin/payment/accounts/${a.id}`, { is_active: !a.is_active });
            toast.success(a.is_active ? "Account deactivated" : "Account activated");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const del = async (a) => {
        if (!window.confirm(`Delete "${a.label}"? Accounts referenced by existing payment links will be deactivated instead.`)) return;
        try {
            const res = await api.delete(`/admin/payment/accounts/${a.id}`);
            toast.success(res.data.action === "deactivated" ? "Deactivated (still referenced by links)" : "Deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                    Manage bank / UPI / Razorpay destinations. The default account is used when creating a link without picking one.
                </p>
                <button
                    onClick={() => setEditing("new")}
                    className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold px-4 py-2 rounded-lg text-sm shadow"
                    data-testid="payment-accounts-new-btn"
                >
                    <Plus className="w-4 h-4" /> Add Account
                </button>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : items.length === 0 ? (
                <div className="py-16 text-center text-slate-500">No payment accounts yet.</div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2">
                    {items.map((a) => (
                        <div
                            key={a.id}
                            className={`bg-white rounded-2xl border p-5 shadow-sm ${a.is_default ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"} ${!a.is_active ? "opacity-60" : ""}`}
                            data-testid={`payment-account-card-${a.id}`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="min-w-0 flex-1">
                                    <div className="font-heading font-bold text-[#0F172A] truncate">{a.label}</div>
                                    <div className="text-xs text-slate-500 truncate">{a.bank_name}</div>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                                    {a.is_default && (
                                        <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
                                            <Star className="w-3 h-3" /> Default
                                        </span>
                                    )}
                                    {!a.is_active && (
                                        <span className="inline-flex items-center gap-1 bg-slate-200 text-slate-700 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">Inactive</span>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2 text-xs">
                                <Line icon={Building2} label="A/C" value={`${a.account_holder} · ${a.account_number}`} />
                                <Line icon={Building2} label="IFSC" value={a.ifsc} mono />
                                <Line icon={Smartphone} label="UPI" value={a.upi_id || "—"} mono />
                                {a.upi_phone && <Line icon={Smartphone} label="UPI Phone" value={a.upi_phone} />}
                                <Line icon={ExternalLink} label="Razorpay" value={a.razorpay_handle ? `razorpay.me/@${a.razorpay_handle}` : "—"} />
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                                {a.is_active && !a.is_default && (
                                    <button
                                        onClick={() => setDefault(a.id)}
                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-50 px-2 py-1 rounded"
                                        data-testid={`payment-account-set-default-${a.id}`}
                                    >
                                        <Star className="w-3 h-3" /> Set as default
                                    </button>
                                )}
                                <button
                                    onClick={() => setEditing(a)}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 px-2 py-1 rounded"
                                    data-testid={`payment-account-edit-${a.id}`}
                                >
                                    <Edit3 className="w-3 h-3" /> Edit
                                </button>
                                <button
                                    onClick={() => toggleActive(a)}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 px-2 py-1 rounded"
                                >
                                    {a.is_active ? <><StarOff className="w-3 h-3" /> Deactivate</> : <><Star className="w-3 h-3" /> Activate</>}
                                </button>
                                <div className="flex-1" />
                                <button
                                    onClick={() => del(a)}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 px-2 py-1 rounded"
                                >
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <AccountEditor
                    account={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function AccountEditor({ account, onClose, onSaved }) {
    const isNew = !account;
    const [form, setForm] = useState({
        label: account?.label || "",
        account_holder: account?.account_holder || "",
        account_number: account?.account_number || "",
        ifsc: account?.ifsc || "",
        bank_name: account?.bank_name || "",
        upi_id: account?.upi_id || "",
        upi_phone: account?.upi_phone || "",
        razorpay_handle: account?.razorpay_handle || "",
        is_active: account?.is_active !== false,
        is_default: !!account?.is_default,
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.label.trim()) { toast.error("Label is required."); return; }
        setSaving(true);
        try {
            if (isNew) {
                await api.post("/admin/payment/accounts", form);
                toast.success("Account created");
            } else {
                await api.patch(`/admin/payment/accounts/${account.id}`, form);
                toast.success("Account updated");
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
            <div
                className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="payment-account-editor"
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
                    <h3 className="font-heading font-bold text-lg text-[#0F172A]">{isNew ? "Add Payment Account" : "Edit Payment Account"}</h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" aria-label="Close"><X className="w-5 h-5 text-slate-500" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <Field label="Label · required" value={form.label} onChange={(v) => setForm({ ...form, label: v })} placeholder="Anand Tutorial · Kotak" required testId="payment-account-editor-label" />
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Account Holder" value={form.account_holder} onChange={(v) => setForm({ ...form, account_holder: v })} />
                        <Field label="Account Number" value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} mono />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="IFSC" value={form.ifsc} onChange={(v) => setForm({ ...form, ifsc: v.toUpperCase() })} mono placeholder="KKBK0005957" />
                        <Field label="Bank Name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="UPI ID" value={form.upi_id} onChange={(v) => setForm({ ...form, upi_id: v })} mono placeholder="anandtutorial@kotak" />
                        <Field label="UPI Phone" value={form.upi_phone} onChange={(v) => setForm({ ...form, upi_phone: v })} placeholder="7974221232" />
                    </div>
                    <Field label="Razorpay Handle" value={form.razorpay_handle} onChange={(v) => setForm({ ...form, razorpay_handle: v })} mono placeholder="anandtutorials — or full razorpay.me/@... URL" />
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <label className="flex items-center gap-2 bg-slate-50 rounded-lg p-3 cursor-pointer">
                            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#1E3A8A]" />
                            <span className="font-semibold text-slate-700">Active</span>
                        </label>
                        <label className="flex items-center gap-2 bg-slate-50 rounded-lg p-3 cursor-pointer">
                            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-amber-500" />
                            <span className="font-semibold text-slate-700">Default</span>
                        </label>
                    </div>
                    <div className="pt-2 flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2.5 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 font-semibold rounded-lg text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold rounded-lg text-sm disabled:opacity-60"
                            data-testid="payment-account-editor-save"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            {isNew ? "Create Account" : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ========================================================================== */

function Field({ label, value, onChange, placeholder, required, mono, textarea, testId }) {
    const commonCls = `w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#1E3A8A] ${mono ? "font-mono" : ""}`;
    return (
        <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">{label}</label>
            {textarea ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={2}
                    required={required}
                    className={commonCls}
                    data-testid={testId}
                />
            ) : (
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    required={required}
                    className={commonCls}
                    data-testid={testId}
                />
            )}
        </div>
    );
}

function Line({ icon: Icon, label, value, mono }) {
    return (
        <div className="flex items-start gap-2">
            <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <span className="text-slate-400 uppercase text-[9px] tracking-widest mr-1.5">{label}</span>
                <span className={`text-slate-800 break-all ${mono ? "font-mono" : ""}`}>{value}</span>
            </div>
        </div>
    );
}

/* ========================================================================== */
/* Mobile card for the Links tab — replaces the wide desktop table on <md      */
/* screens. Big amount + status + tap-optimised action buttons so admins can   */
/* create, share, copy and mark-paid entirely from their phone.                */
/* ========================================================================== */

function MobileLinkCard({ item: it, url, onStatus, onDelete, onEdit, onNewFrom, onHistory }) {
    const StatusIcon = STATUS_PILLS[it.status]?.icon || Clock;

    const copyLink = () => {
        try {
            navigator.clipboard.writeText(url);
            toast.success("Link copied — paste it anywhere");
        } catch {
            toast.error("Long-press the link and copy manually");
        }
    };

    // V173.3 — Pre-address wa.me when the link has a stored contact number;
    // otherwise fall back to the generic share sheet so admin can pick a chat.

    return (
        <div
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3"
            data-testid={`payment-link-card-${it.id}`}
        >
            {/* Amount + status row */}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="font-heading text-2xl font-black tabular-nums text-[#0F172A]">
                        {fmtInr(it.amount)}
                    </div>
                    {it.session_count && <div className="text-xs text-slate-600 font-semibold" data-testid={`payment-link-card-sessions-${it.id}`}>{it.session_count} sessions</div>}
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">{it.id}</div>
                </div>
                <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold shrink-0 ${STATUS_PILLS[it.status]?.cls || ""}`}
                >
                    <StatusIcon className="w-3 h-3" /> {STATUS_PILLS[it.status]?.label}
                </span>
            </div>

            {/* Meta */}
            {(it.client_search_name || it.student_name || it.parent_name || it.description || it.enquiry_id || it.contact_number) && (
                <div className="text-sm text-slate-700 space-y-0.5 border-t border-slate-100 pt-3">
                    {it.client_search_name && (
                        <div className="flex flex-wrap items-center gap-1.5" data-testid={`payment-link-card-client-name-${it.id}`}>
                            <span className="font-semibold text-violet-800 break-words">{it.client_search_name}</span><AdminOnlyBadge />
                        </div>
                    )}
                    {(it.student_name || it.parent_name) && (
                        <div className="font-medium">{it.student_name || it.parent_name}</div>
                    )}
                    {it.contact_number && (
                        <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1" data-testid={`payment-link-card-contact-${it.id}`}>
                            <MessageCircle className="w-3 h-3" /> Direct → +{it.contact_number}
                        </div>
                    )}
                    {it.description && <div className="text-xs text-slate-500 italic leading-relaxed">{it.description}</div>}
                    {it.enquiry_id && <div className="text-[10px] text-slate-400 uppercase tracking-wider">Enquiry · {it.enquiry_id}</div>}
                </div>
            )}

            {/* Account + timestamps */}
            <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                {it.payment_account_label && <span>💳 {it.payment_account_label}</span>}
                <span>🕒 {fmtDate(it.created_at)}</span>
            </div>

            {/* Signals — parent-confirmed / screenshot */}
            {(it.parent_confirmed_at || it.has_screenshot) && (
                <div className="flex flex-wrap gap-2">
                    {it.parent_confirmed_at && it.status === "pending" && (
                        <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Parent confirmed
                        </span>
                    )}
                    {it.has_screenshot && (
                        <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-semibold px-2 py-1 rounded-full">
                            <ImageIcon className="w-3 h-3" /> Screenshot
                        </span>
                    )}
                </div>
            )}

            {/* Primary actions — Copy + WhatsApp (always visible, full-width on mobile) */}
            <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                    onClick={copyLink}
                    className="inline-flex items-center justify-center gap-2 bg-[#1E3A8A] hover:bg-[#1E40AF] active:scale-[0.98] text-white font-semibold px-3 py-2.5 rounded-lg text-sm shadow"
                    data-testid={`payment-link-copy-${it.id}`}
                >
                    <Copy className="w-4 h-4" /> Copy Link
                </button>
                <a
                    href={waHref(it)}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] active:scale-[0.98] text-white font-semibold px-3 py-2.5 rounded-lg text-sm shadow"
                    data-testid={`payment-link-whatsapp-${it.id}`}
                >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
            </div>

            {/* Secondary actions — Open + status controls (only for pending links) */}
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3 -mx-1 px-1">
                <a
                    href={url}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 px-2 py-1.5 rounded"
                >
                    <ExternalLink className="w-3.5 h-3.5" /> Preview
                </a>
                <button onClick={() => onHistory(it)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 px-2 py-1.5 rounded" data-testid={`payment-link-card-history-${it.id}`}>
                    <Clock className="w-3.5 h-3.5" /> History
                </button>
                {it.status === "pending" ? (
                    <button onClick={() => onEdit(it)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E3A8A] hover:bg-blue-50 px-2 py-1.5 rounded" data-testid={`payment-link-card-edit-${it.id}`}>
                        <Edit3 className="w-3.5 h-3.5" /> Edit
                    </button>
                ) : (
                    <button onClick={() => onNewFrom(it)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E3A8A] hover:bg-blue-50 px-2 py-1.5 rounded" data-testid={`payment-link-card-new-from-${it.id}`}>
                        <Plus className="w-3.5 h-3.5" /> New request
                    </button>
                )}
                {it.status === "pending" && (
                    <>
                        <button
                            onClick={() => onStatus(it.id, "paid")}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 px-2 py-1.5 rounded"
                            data-testid={`payment-link-mark-paid-${it.id}`}
                        >
                            <Check className="w-3.5 h-3.5" /> Mark Paid
                        </button>
                        <button
                            onClick={() => onStatus(it.id, "cancelled")}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 px-2 py-1.5 rounded"
                        >
                            <Ban className="w-3.5 h-3.5" /> Cancel
                        </button>
                        <div className="flex-1" />
                        <button
                            onClick={() => onDelete(it.id)}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 px-2 py-1.5 rounded"
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
